import { useCallback, useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import api from '../../api'
import Button from '../../ui/Button'
import Modal from '../../ui/Modal'
import PageContent from '../../ui/PageContent'
import { Table, useSearchTableQueryState } from '../../components'
import { ArchiveIcon, DuplicateIcon, EditIcon, FolderMoveIcon, PlusIcon, RestartIcon, TrashIcon } from '../../ui/icons'
import { JourneyForm } from './JourneyForm'
import { Menu, MenuItem, Tag } from '../../ui'
import { ProjectContext } from '../../contexts'
import { useTranslation } from 'react-i18next'
import { Folder, Journey } from '../../types'
import { TagPicker } from '../settings/TagPicker'
import toast from 'react-hot-toast/headless'
import { FolderSelectModal } from '../../components/table/FolderSelectModal'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { checkProjectRole } from '../../utils'

export const JourneyTag = ({ status, deleted_at }: Pick<Journey, 'status' | 'deleted_at'>) => {
    const { t } = useTranslation()
    if (deleted_at) return <Tag variant="error">{t('archived')}</Tag>
    const variant = status === 'live' ? 'success' : 'plain'
    return <Tag variant={variant}>{t(status)}</Tag>
}

const journeyStatuses = (t: (key: string) => string) => [
    { key: 'live', label: t('live') },
    { key: 'off', label: t('off') },
    { key: 'draft', label: t('draft') },
    { key: 'archived', label: t('archived') },
]

export default function Journeys() {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const state = useSearchTableQueryState(useCallback(async params => await api.journeys.search(project.id, params), [project.id]))
    const [open, setOpen] = useState<null | 'create'>(null)
    const [journeyToMove, setJourneyToMove] = useState<Journey | undefined>()

    const isPublisher = checkProjectRole('publisher', project.role)

    const handleEditJourney = async (id: number) => {
        await navigate(id.toString())
    }

    const handleMoveFolder = async (folder: Folder) => {
        if (!journeyToMove) return
        const { name, status } = journeyToMove
        await api.journeys.update(project.id, journeyToMove.id, { name, status, folder_id: folder.id })
        setJourneyToMove(undefined)
        await state.reload()
        toast.success(t('folder_moved_item', { item: journeyToMove.name, folder: folder.name }))
    }

    const handleDuplicateJourney = async (id: number) => {
        const journey = await api.journeys.duplicate(project.id, id)
        await navigate(journey.id.toString())
    }

    const handleArchiveJourney = async (id: number) => {
        await api.journeys.delete(project.id, id)
        await state.reload()
    }

    const handleRestoreJourney = async (id: number) => {
        if (!confirm(t('journey_restore_confirm'))) return
        await api.journeys.restore(project.id, id)
        await state.reload()
        toast.success(t('journey_restored'))
    }

    const handleDeleteJourney = async (id: number) => {
        if (!confirm(t('journey_delete_permanently_confirm'))) return
        await api.journeys.delete(project.id, id)
        await state.reload()
    }

    const emptyMessage = Object.keys(state.params.filter ?? {}).some(key => key !== 'folder_id')
        ? t('journeys_empty_filter', {
            filters: Object.keys(state.params.filter ?? {})
                .filter(k => k !== 'folder_id')
                .map(k => t(k).toLowerCase())
                .join(', '),
        })
        : undefined

    return (
        <PageContent
            title={t('journeys')}
            actions={
                <Button icon={<PlusIcon />} onClick={() => setOpen('create')}>{t('create_journey')}</Button>
            }
        >
            <Table
                {...state}
                type="folder"
                columns={[
                    {
                        key: 'name',
                        title: t('name'),
                        minWidth: '150px',
                        cell: ({ item: { name, description } }) => (
                            <div className="multi-cell no-image">
                                <div className="text">
                                    <div className="title">{name}</div>
                                    {description && <div className="subtitle">
                                        {description}</div>}
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'status',
                        title: t('status'),
                        cell: ({ item }) => <JourneyTag status={item.status} deleted_at={item.deleted_at} />,
                    },
                    {
                        key: 'created_at',
                        title: t('created_at'),
                    },
                    {
                        key: 'updated_at',
                        title: t('updated_at'),
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => (
                            <Menu size="small">
                                <MenuItem icon={<EditIcon />} onClick={async () => await handleEditJourney(item.id)}>
                                    {t('edit')}
                                </MenuItem>
                                {!item.deleted_at && <MenuItem icon={<FolderMoveIcon />} onClick={async () => setJourneyToMove(item)}>
                                    {t('folder_move')}
                                </MenuItem>}
                                {!item.deleted_at && <MenuItem icon={<DuplicateIcon />} onClick={async () => await handleDuplicateJourney(item.id)}>
                                    {t('duplicate')}
                                </MenuItem>}
                                {!item.deleted_at && <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchiveJourney(item.id)}>
                                    {t('archive')}
                                </MenuItem>}
                                {!!item.deleted_at && isPublisher && <MenuItem icon={<RestartIcon />} onClick={async () => await handleRestoreJourney(item.id)}>
                                    {t('journey_restore')}
                                </MenuItem>}
                                {!!item.deleted_at && <MenuItem icon={<TrashIcon />} onClick={async () => await handleDeleteJourney(item.id)}>
                                    {t('journey_delete_permanently')}
                                </MenuItem>}
                            </Menu>
                        ),
                    },
                ]}
                onSelectRow={async r => { await navigate(r.id.toString()) }}
                enableSearch
                emptyMessage={emptyMessage}
                filters={[
                    <MultiSelect
                        key="status"
                        size="small"
                        options={journeyStatuses(t)}
                        value={state.params.filter?.status}
                        onChange={value => state.setParams({
                            ...state.params,
                            filter: {
                                ...state.params.filter,
                                status: value.length ? value : undefined,
                            },
                        })}
                        placeholder={t('status')}
                        toValue={v => v.key}
                    />,
                    <TagPicker
                        key="tags"
                        entity="journeys"
                        size="small"
                        value={state.params.filter?.tags ?? []}
                        onChange={value => state.setParams({
                            ...state.params,
                            filter: {
                                ...state.params.filter,
                                tags: value.length ? value : undefined,
                            },
                        })}
                        placeholder={t('tags')}
                    />,
                ]}
            />
            <Modal
                onClose={() => setOpen(null)}
                open={!!open}
                title={t('create_journey')}
            >
                <JourneyForm
                    folderId={state.params.filter?.folder_id}
                    onSaved={async journey => {
                        setOpen(null)
                        await navigate(journey.id.toString())
                    }}
                />
            </Modal>

            {!!journeyToMove && <FolderSelectModal
                type="journey"
                open={!!journeyToMove}
                onClose={() => setJourneyToMove(undefined)}
                onSelect={handleMoveFolder}
            />}
        </PageContent>
    )
}
